import React, { useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash-es/debounce';
import { getIsExistingSr } from 'common/utils/srUtils';
import { ReactComponent as PrevIcon } from 'images/icons/prev.svg';
import { ReactComponent as NextIcon } from 'images/icons/next.svg';
import { useSrById } from 'remote-state/ticketServiceHooks';
import { QUERIES_KEYS } from 'constant';

const NAVIGATION_CLICK_INTERVAL = 300;

interface Props {
    srId: number;
}

interface DataItem {
    value: number;
}

interface Data {
    serviceRequests: DataItem[][];
}

const NextOrPrevSR: React.FC<Props> = ({ srId }): JSX.Element | null => {
    const queryClient = useQueryClient();
    const router = useRouter();
    const isExistingSR = getIsExistingSr(srId);
    const queryCache = queryClient.getQueryCache();

    const loadedSrIds = useMemo((): number[] => {
        const queueDataCachedQueries = queryCache.getAll()
            .filter(query => query.queryKey.some(key => key === QUERIES_KEYS.QUEUE_DATA));

        let srIds: number[] = [];
        for (const query of queueDataCachedQueries) {
            const srIdsList = ((query?.state?.data as Data)?.serviceRequests || []).map((item) => item[0]?.value) || [];
            srIds = [
                ...srIds,
                ...srIdsList,
            ];
        }
        return srIds;
    }, [queryCache]);

    const index = useMemo((): number => loadedSrIds?.findIndex((item) => item === srId), [loadedSrIds, srId]);

    const isNextDisable = useMemo((): boolean => index === loadedSrIds?.length - 1, [index, loadedSrIds]);

    const isPrevDisable = useMemo((): boolean => index === 0, [index]);

    useSrById(loadedSrIds?.length && !isNextDisable && loadedSrIds[index + 1]);
    useSrById(loadedSrIds?.length && !isPrevDisable && loadedSrIds[index - 1]);

    const { srType } = useSrById(srId)?.data ?? {};

    const onPrevClick = debounce(() => {
        router.navigate({
            to: '/spaces/ticket',
            search: { id: loadedSrIds[index - 1] },
        });
    }, NAVIGATION_CLICK_INTERVAL);

    const onNextClick = debounce(() => {
        router.navigate({
            to: '/spaces/ticket',
            search: { id: loadedSrIds[index + 1] },
        });
    }, NAVIGATION_CLICK_INTERVAL);

    const hasNavigation = Boolean(loadedSrIds?.length) && isExistingSR && (index !== -1 || !Number.isNaN(srType));

    if (!hasNavigation) {
        return null;
    }

    return (
        <div className='next-or-prev-sr'>
            <button onClick={onPrevClick} disabled={isPrevDisable} data-testid='prev-sr'>
                <PrevIcon />
            </button>
            <button onClick={onNextClick} disabled={isNextDisable} data-testid='next-sr'>
                <NextIcon />
            </button>
        </div>
    );
}

export default NextOrPrevSR;
